import { useMemo, useState } from 'react';
import { SavedProduct } from '@croquiscom/web2app';

import { useAppConfig } from '@common/app-config';
import { SwiperClass } from '@common/carousel/swiper';
import { convertToPDPDeepLink, convertToWebPDPLink } from '@common/pdp';
import {
  formatCategoryList,
  useInitLikedProducts,
  useLikeProductService,
  type WebLikeProductPayload,
} from '@widgets/like-product';
import {
  convertColorToSelectedPdp,
  PDPGoodsGroup_goods_carousel_component_list as ProductCardItem,
  useCatalogProductIdParams,
  useDocumentVisibleOnce,
  usePdpBaseInfo,
  usePdpRelatedProductRecommendGroups,
} from '@domains/pdp-common';

import { useRelatedStylistListUBL } from '../log/useRelatedStylistListUBL';

export const PAGE_VIEW_COUNT = 2;

const convertGoodsList = (list: SavedProduct[]) => list.map(({ catalogProductId }) => catalogProductId || '');

export const useRelatedStylistListService = () => {
  const { isZigzagApp } = useAppConfig();
  const { ref, isPermanentlyOnce } = useDocumentVisibleOnce();
  const { onLikeProduct } = useLikeProductService();
  const params = useCatalogProductIdParams();
  const {
    product: { id },
    flags: { is_selected_shop },
    key_color: { discount_info_of_atf },
  } = usePdpBaseInfo(params);
  const { clickRelatedItem, impressionRelatedItem, savedProductRelatedItem } = useRelatedStylistListUBL(id);
  const { seller_coordi } = usePdpRelatedProductRecommendGroups({
    catalog_product_id: params.catalog_product_id,
    isSelectedShop: Boolean(is_selected_shop),
  });
  const [currentIndex, setCurrentIndex] = useState(0);

  convertColorToSelectedPdp(seller_coordi, discount_info_of_atf);

  const list = seller_coordi[0]?.goods_carousel?.component_list ?? [];
  const groupType = seller_coordi[0]?.group_type ?? '';
  const mainTitle = seller_coordi[0]?.main_title.text ?? '';

  const totalPage = Math.round(list.length / PAGE_VIEW_COUNT);
  const isSinglePage = totalPage === 1;
  const isSingleItem = list.length === 1;

  const classnameTotalPage = isSinglePage ? 'stylist-single-page' : 'stylist-multi-page';
  const goodsList = useMemo(() => {
    return seller_coordi.reduce<SavedProduct[]>((acc, { goods_carousel: { component_list } }) => {
      const picked = component_list.map(({ shop_id, shop_product_no, catalog_product_id, is_saved_product }) => ({
        is_saved_product,
        shopId: Number(shop_id),
        shopProductNo: shop_product_no,
        catalogProductId: catalog_product_id,
      }));
      return acc.concat(picked);
    }, []);
  }, [seller_coordi]);

  useInitLikedProducts({
    data: goodsList,
    convert: convertGoodsList,
  });

  const onSavedProduct = (checked: boolean, item: ProductCardItem, position: number) => {
    const inAppLikeProductLog = {
      object_idx: item.position,
      object_section: groupType,
      main_title: mainTitle.replace('\n', ' '),
      original_product_id: id,
    };

    const inAppLikeProductPayload = {
      like: checked,
      browsingType: item.browsing_type,
      shopId: Number(item.shop_id),
      shopProductNo: item.shop_product_no,
      catalogProductId: item.catalog_product_id,
      title: item.title || '',
      goodsUrl: item.product_url || '',
      imageUrl: item.image_url || '',
      price: Number(item.price),
      discountRate: Number(item.discount_rate),
      isZpay: Boolean(item.zpay),
      isFreeShipping: Boolean(item.free_shipping),
      log: inAppLikeProductLog,
    };

    const airbridge: WebLikeProductPayload['airbridge'] = {
      brandID: item.shop_id,
      brandName: item.shop_name,
      productID: item.catalog_product_id,
      productName: item.title,
      price: item.final_price,
      categoryID: formatCategoryList(item.managed_category_list, 'id'),
      categoryName: formatCategoryList(item.managed_category_list, 'value'),
    };

    const webLikeProductPayload: WebLikeProductPayload = {
      checked,
      productData: {
        catalog_product_id: item.catalog_product_id || '',
        shop_id: item.shop_id || '',
      },
      airbridge,
      sendLog: (checked: boolean) =>
        savedProductRelatedItem({
          category: checked ? 'save_product' : 'remove_saved_product',
          position,
          groupType,
          item,
        }),
    };

    onLikeProduct({
      inAppLikeProductPayload,
      webLikeProductPayload,
    });
  };

  const handleClick = (item: ProductCardItem, position: number) => {
    clickRelatedItem({
      position,
      groupType,
      item,
    });

    const url = isZigzagApp
      ? convertToPDPDeepLink({
          browsing_type: item.browsing_type,
          product_url: item.product_url,
          shop_id: item.shop_id,
          shop_product_no: item.shop_product_no,
          catalog_product_id: item.catalog_product_id,
        })
      : convertToWebPDPLink({ product_url: item.product_url });
    window.location.assign(url);
  };

  const handleImpression = (item: ProductCardItem, position: number) => {
    impressionRelatedItem({ groupType, position, item });
  };

  const handleSlideChange = (swiper: SwiperClass) => {
    setCurrentIndex(swiper.activeIndex);
  };

  return {
    targetRef: ref,
    mainTitle,
    visible: isPermanentlyOnce,
    classnameTotalPage,
    isSinglePage,
    isSingleItem,
    list: list.map(coverter),
    currentIndex,
    handleSlideChange,
    onSavedProduct,
    handleClick,
    handleImpression,
  };
};

function coverter(item: ProductCardItem): ProductCardItem {
  return {
    ...item,
    // PDP 이 상품과 함께 착용한 코디 상품 영역에는 리뷰가 미노출되어 분기 처리 추가
    review_score: null,
    display_review_count: null,
  };
}
